import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Button,
  CardMedia,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Input,
  TextField,
} from "@mui/material";

import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";

import {
  useAddPromotionMutation,
  useUpdatePromotionMutation,
  useGetPromotionByIdQuery,
} from "../../services/promotions";
import { useGetUserListQuery } from "../../services/user";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/uploadMedia";
import EditText from "../../components/editText";
import usePermission from "../../hooks/userAccess";

const usersList = [
  { label: "Select All Users", id: "all" },
  { label: "John Doe", id: "1" },
  { label: "Jane Smith", id: "2" },
  { label: "Michael Scott", id: "3" },
  { label: "Pam Beesly", id: "4" },
];

const AddPromotion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdd, isDelete, isView } = usePermission({
    label: "Online Brands Management",
    page_permission: "isAdd",
  });

  const { id: routeId } = useParams();
  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(true);
  const [selectedUsers, setSelectedUsers] = React.useState<typeof usersList>(
    []
  );
  const { data: usersRes } = useGetUserListQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  const dynamicUsers = useMemo(() => {
    const list =
      (usersRes as any)?.data?.list ||
      (Array.isArray((usersRes as any)?.data) ? (usersRes as any).data : []);
    return list.map((u: any) => ({
      label: u?.fullName || u?.email || u?._id,
      id: u?._id,
    })) as typeof usersList;
  }, [usersRes]);

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [fileName, setFileName] = useState<string>("");
  const [legal2, setLegal2] = useState<string>("");
  const [legal2Ar, setLegal2Ar] = useState<string>("");

  const isAddPromotion = location.pathname === "/manage-promotions/add";
  const isEdit = Boolean(routeId);
  const { data: promoByIdRes, isFetching: isFetchingPromo } =
    useGetPromotionByIdQuery(
      isEdit ? { id: String(routeId) } : ({ id: "" } as any),
      { skip: !isEdit }
    );
  const editingData = useMemo(() => {
    if (promoByIdRes && (promoByIdRes as any).data)
      return (promoByIdRes as any).data;
    return (location as any)?.state?.promotion || null;
  }, [promoByIdRes, location]);

  useEffect(() => {
    // Prefill from existing promotion when editing
    if (isEdit) {
      const tc = (editingData as any)?.termsConditions || "";
      setLegal2(tc);
      const tcAr = (editingData as any)?.termsConditions_ar || "";
      setLegal2Ar(tcAr);
    }
  }, [isEdit, editingData]);

  const [addPromotion, { isLoading: isSaving }] = useAddPromotionMutation();
  const [updatePromotion, { isLoading: isUpdating }] =
    useUpdatePromotionMutation();

  const stripHtmlTags = (value: string | undefined | null): string => {
    if (!value) return "";
    return value.replace(/<[^>]*>/g, "").trim();
  };

  const asOptionalString = (schema: any) =>
    schema
      .transform((value: unknown, originalValue: unknown) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .notRequired();

  const validationSchema = Yup.object({
    image: Yup.string().required("Image is required"),
    title: Yup.string()
      .required("Title is required")
      .min(3, "Title must be at least 3 characters")
      .max(100, "Title must be less than 100 characters"),
    title_ar: asOptionalString(
      Yup.string()
        .min(3, "Arabic title must be at least 3 characters")
        .max(100, "Arabic title must be less than 100 characters")
    ),
    previewText: asOptionalString(Yup.string()),
    shopOnline: Yup.boolean().notRequired(),
    type: Yup.number().nullable().notRequired(),
    isDeleted: Yup.boolean().notRequired(),
    category: Yup.number().nullable().notRequired(),
    description: asOptionalString(
      Yup.string()
        .test(
          "min-length",
          "Description must be at least 10 characters",
          function (this: any, value: any) {
            if (!value) return true;
            const textContent = stripHtmlTags(value);
            return textContent.length >= 10;
          }
        )
        .test(
          "max-length",
          "Description must be less than 1000 characters",
          function (this: any, value: any) {
            if (!value) return true;
            const textContent = stripHtmlTags(value);
            return textContent.length <= 1000;
          }
        )
    ),
    description_ar: asOptionalString(
      Yup.string()
        .test(
          "min-length",
          "Arabic description must be at least 10 characters",
          function (this: any, value: any) {
            if (!value) return true;
            const textContent = stripHtmlTags(value);
            return textContent.length >= 10;
          }
        )
        .test(
          "max-length",
          "Arabic description must be less than 1000 characters",
          function (this: any, value: any) {
            if (!value) return true;
            const textContent = stripHtmlTags(value);
            return textContent.length <= 1000;
          }
        )
    ),
    termsConditions: asOptionalString(
      Yup.string()
      // .test(
      //   "min-length",
      //   "Terms & Conditions must be at least 20 characters",
      //   function (this: any, value: any) {
      //     if (!value) return true;
      //     const textContent = stripHtmlTags(value);
      //     return textContent.length >= 20;
      //   }
      // )
      // .test(
      //   "max-length",
      //   "Terms & Conditions must be less than 2000 characters",
      //   function (this: any, value: any) {
      //     if (!value) return true;
      //     const textContent = stripHtmlTags(value);
      //     return textContent.length <= 2000;
      //   }
      // )
    ),
    termsConditions_ar: asOptionalString(
      Yup.string()
      // .test(
      //   "min-length",
      //   "Arabic Terms & Conditions must be at least 20 characters",
      //   function (this: any, value: any) {
      //     if (!value) return true;
      //     const textContent = stripHtmlTags(value);
      //     return textContent.length >= 20;
      //   }
      // )
      // .test(
      //   "max-length",
      //   "Arabic Terms & Conditions must be less than 2000 characters",
      //   function (this: any, value: any) {
      //     if (!value) return true;
      //     const textContent = stripHtmlTags(value);
      //     return textContent.length <= 2000;
      //   }
      // )
    ),
    url: asOptionalString(Yup.string().url("Please enter a valid URL")),
    id: Yup.string().optional(),
  });

  // Initial values
  const getInitialValues = () => ({
    image: editingData?.image || "",
    title: editingData?.title || "",
    title_ar: editingData?.title_ar || "",
    previewText: editingData?.previewText || "",
    shopOnline: Boolean(editingData?.shopOnline) || false,
    type: 3,
    isDeleted: Boolean(editingData?.isDeleted) || false,
    category: Number(editingData?.category) || 1,
    description: editingData?.description || "",
    description_ar: editingData?.description_ar || "",
    termsConditions: editingData?.termsConditions || "",
    termsConditions_ar: editingData?.termsConditions_ar || "",
    url: editingData?.url || "",
  });

  const handleImageUpload = async (file: any, setFieldValue: any) => {
    const res = await UploadMedia(file);
    const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
    if (imageUrl) {
      setFieldValue("image", imageUrl);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>{isAddPromotion ? "Add " : "Edit "} Online Brand Promotion</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate(-1)}>Online Brands Management</button>{" "}
          - {isAddPromotion ? "Add " : "Edit "} Online Brand Promotion
        </p>
      </div>

      <div className="cards">
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          validateOnMount={false}
          onSubmit={async (
            values,
            { setSubmitting, setFieldError, setTouched, validateForm }
          ) => {
            // Mark all fields as touched to show validation errors
            setTouched({
              title: true,
              title_ar: true,
              previewText: true,
              shopOnline: true,
              description: true,
              description_ar: true,
              termsConditions: true,
              termsConditions_ar: true,
              url: true,
            });
            const validationErrors = await validateForm();
            if (Object.keys(validationErrors).length > 0) {
              setSubmitting(false);
              return;
            }

            try {
              setSubmitting(true);

              if (isAddPromotion) {
                await addPromotion({
                  ...(values as any),
                  termsConditions: legal2,
                  termsConditions_ar: legal2Ar,
                } as any).unwrap();
              } else {
                await updatePromotion({
                  body: {
                    ...values,
                    termsConditions: legal2,
                    termsConditions_ar: legal2Ar,
                    id: routeId,
                  } as any,
                }).unwrap();
              }

              navigate(-1);
            } catch (error: any) {
              // Handle specific field errors if API returns them
              if (error?.data?.errors) {
                Object.keys(error.data.errors).forEach((field) => {
                  setFieldError(field, error.data.errors[field]);
                });
              } else {
                setAlertType(0);
                setShowAlert(true);
                setAlertMessage(
                  "An error occurred while saving. Please try again."
                );
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form className="form" onSubmit={handleSubmit}>
              <div className="gap_p">
                {/* Image Upload */}
                <div className="control_group w_100">
                  <div className="gap_p">
                    <div className="control_group w_33">
                      <label htmlFor="icon-button-file">
                        Upload Image <span style={{ color: "red" }}>*</span>
                      </label>
                      {values.image ? (
                        <div className="upload_image v2 no_radius">
                          <div className="upload_image_holder">
                            <figure>
                              <CardMedia
                                component="img"
                                image={values.image}
                                alt="Uploaded promotion image"
                              />
                            </figure>
                            <IconButton
                              aria-label="Remove uploaded image"
                              onClick={() => {
                                setFieldValue("image", "");
                                setFileName("");
                              }}
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        <label
                          className="upload_image v2 no_radius"
                          htmlFor="icon-button-file"
                        >
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{
                              accept: "image/png,image/jpeg,image/jpg",
                            }}
                            onChange={(
                              event: ChangeEvent<HTMLInputElement>
                            ) => {
                              const files = (event.target as HTMLInputElement)
                                .files;
                              if (files && files[0]) {
                                if (files[0].type.includes("image")) {
                                  // Validate file size (max 5MB)
                                  if (files[0].size > 5 * 1024 * 1024) {
                                    setAlertType(0);
                                    setShowAlert(true);
                                    setAlertMessage(
                                      "Image size must be less than 5MB."
                                    );
                                    return;
                                  }
                                  setFileName(files[0].name);
                                  handleImageUpload(files[0], setFieldValue);
                                } else {
                                  setAlertType(0);
                                  setShowAlert(true);
                                  setAlertMessage(
                                    "This field only accepts images (PNG, JPEG, JPG)."
                                  );
                                }
                              }
                            }}
                          />
                          <span className="upload_image_holder">
                            <figure className="add_img">
                              <img
                                src="/static/images/placeholder.png"
                                alt="placeholder"
                              />
                              <span className="upload_img_icon">
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="Upload"
                                />
                              </span>
                            </figure>
                          </span>
                        </label>
                      )}
                      {touched.image && errors.image && (
                        <FormHelperText error>
                          {String(errors.image)}
                        </FormHelperText>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="control_group w_33">
                  <label htmlFor="title">
                    Title <span style={{ color: "red" }}>*</span>
                  </label>
                  <TextField
                    id="title"
                    name="title"
                    placeholder="Enter Title"
                    hiddenLabel
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={touched.title && Boolean(errors.title)}
                    helperText={
                      touched.title && errors.title ? String(errors.title) : ""
                    }
                  />
                </div>

                {/* Arabic Title */}
                <div className="control_group w_33">
                  <label htmlFor="title_ar">Arabic Title</label>
                  <TextField
                    id="title_ar"
                    name="title_ar"
                    placeholder="Enter Arabic Title"
                    hiddenLabel
                    fullWidth
                    value={values.title_ar}
                    onChange={handleChange}
                    error={touched.title_ar && Boolean(errors.title_ar)}
                    helperText={
                      touched.title_ar && errors.title_ar
                        ? String(errors.title_ar)
                        : ""
                    }
                  />
                </div>

                {/* URL */}
                <div className="control_group w_33">
                  <label htmlFor="url">
                    URL <span> (for external articles)</span>
                  </label>
                  <TextField
                    id="url"
                    name="url"
                    placeholder="Enter URL (https://...)"
                    hiddenLabel
                    fullWidth
                    value={values.url}
                    onChange={handleChange}
                    error={touched.url && Boolean(errors.url)}
                    helperText={
                      touched.url && errors.url ? String(errors.url) : ""
                    }
                  />
                </div>

                {/* Preview Text */}
                <div className="control_group w_33">
                  <label htmlFor="previewText">Preview Text</label>
                  <TextField
                    id="previewText"
                    name="previewText"
                    placeholder="Enter preview text"
                    hiddenLabel
                    fullWidth
                    value={values.previewText}
                    onChange={handleChange}
                    error={touched.previewText && Boolean(errors.previewText)}
                    helperText={
                      touched.previewText && errors.previewText
                        ? String(errors.previewText)
                        : ""
                    }
                  />
                </div>

                {/* Shop Online */}
                <div className="control_group w_33">
                  <label>Shop Online</label>
                  <FormGroup row sx={{ columnGap: "16px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(values.shopOnline)}
                          onChange={() => setFieldValue("shopOnline", true)}
                          name="shopOnlineYes"
                        />
                      }
                      label="Yes"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!Boolean(values.shopOnline)}
                          onChange={() => setFieldValue("shopOnline", false)}
                          name="shopOnlineNo"
                        />
                      }
                      label="No"
                    />
                  </FormGroup>
                </div>

                {/* Description */}
                <div
                  className="control_group w_100"
                  style={{ direction: "ltr" }}
                >
                  <label htmlFor="description">Description</label>
                  {isEdit && !values.description ? null : (
                    <EditText
                      content={values.description || " "}
                      setContent={(val: string) => {
                        setFieldValue("description", val);
                        setFieldTouched("description", true, true);
                      }}
                    />
                  )}
                  {touched.description && errors.description && (
                    <FormHelperText error>
                      {String(errors.description)}
                    </FormHelperText>
                  )}
                </div>

                {/* Arabic Description */}
                <div className="control_group" style={{ direction: "ltr" }}>
                  <label htmlFor="description_ar">Arabic Description</label>
                  {isEdit && !values.description_ar ? null : (
                    <EditText
                      content={values.description_ar || " "}
                      setContent={(val: string) => {
                        setFieldValue("description_ar", val);
                        setFieldTouched("description_ar", true, true);
                      }}
                    />
                  )}
                  {touched.description_ar && errors.description_ar && (
                    <FormHelperText error>
                      {String(errors.description_ar)}
                    </FormHelperText>
                  )}
                </div>

                {/* Terms & Conditions */}
                <div
                  className="control_group w_100"
                  style={{ direction: "ltr" }}
                >
                  <label htmlFor="termsConditions">Terms & Conditions</label>
                  {isEdit && !values.termsConditions ? null : (
                    <EditText
                      content={values.termsConditions || " "}
                      setContent={(val: string) => {
                        setFieldValue("termsConditions", val);
                        setFieldTouched("termsConditions", true, true);
                      }}
                    />
                  )}
                  {touched.termsConditions && errors.termsConditions && (
                    <FormHelperText error>
                      {String(errors.termsConditions)}
                    </FormHelperText>
                  )}
                </div>

                {/* Arabic Terms & Conditions */}
                <div className="control_group" style={{ direction: "ltr" }}>
                  <label htmlFor="description_ar">
                    Arabic Terms & Conditions
                  </label>
                  {isEdit && !values.termsConditions_ar ? null : (
                    <EditText
                      content={values.termsConditions_ar || " "}
                      setContent={(val: string) => {
                        setFieldValue("termsConditions_ar", val);
                        setFieldTouched("termsConditions_ar", true, true);
                      }}
                    />
                  )}
                  {touched.termsConditions_ar && errors.termsConditions_ar && (
                    <FormHelperText error>
                      {String(errors.termsConditions_ar)}
                    </FormHelperText>
                  )}
                </div>
              </div>

              {isAdd && (
                <div className="form_btn">
                  <Button
                    className="btn btn_primary"
                    type="submit"
                    disabled={isSaving || isUpdating || isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AddPromotion;
